// ---- Date rules ----
// How a recurring item's date-of-month gets resolved to an actual calendar date.
export type DateRuleType = 'exact' | 'working_day_before' | 'working_day_after'

export interface DateRule {
  type: DateRuleType
  /** 1-31, or 'last' to mean the last day of the month */
  dayOfMonth: number | 'last'
}

export type Frequency = 'monthly' | 'weekly' | 'four_weekly' | 'yearly'

// ---- Recurring income ----
export interface IncomeRule {
  id: string
  kind: 'income'
  name: string
  amount: number // always positive
  frequency: Frequency
  dateRule: DateRule // used when frequency is monthly/yearly
  monthOfYear?: number // 1 (Jan) - 12 (Dec), used when frequency === 'yearly'
  dayOfWeek?: number // 0 (Sun) - 6 (Sat), used when frequency is weekly/four_weekly
  startDate?: string // ISO yyyy-mm-dd, optional
  endDate?: string // ISO yyyy-mm-dd, optional
  active: boolean
  category?: string
  notes?: string
  /**
   * Merchants statement imports match to this income — see BudgetItem.matchPatterns.
   * The amount is what's expected; an occurrence's ActualEntry is what really came in.
   */
  matchPatterns?: string[]
}

// ---- Recurring fixed outgoing (bills, loans, subscriptions) ----
export interface ExpenseRule {
  id: string
  kind: 'expense'
  name: string
  amount: number // always positive (money going out)
  frequency: Frequency
  dateRule: DateRule
  monthOfYear?: number // e.g. an insurance premium paid annually in March
  dayOfWeek?: number
  startDate?: string
  endDate?: string // e.g. final loan repayment date
  active: boolean
  category?: string
  /** Merchants statement imports match to this outgoing — see BudgetItem.matchPatterns. */
  matchPatterns?: string[]
  // Loan / interest-bearing debt fields (optional — only relevant to debts)
  isDebt?: boolean
  interestRateApr?: number // annual percentage rate, e.g. 6.9
  principal?: number // current outstanding balance, for interest & payoff modelling
  /** The date `principal` is true on. Capital repayments from this date reduce it (SM-41). */
  principalAsOf?: string
  /** Overpayments that reduce the balance without clearing it (SM-41). */
  capitalRepayments?: CapitalRepayment[]
  /**
   * Dated changes to `amount`: from each `from` date (inclusive) the payment is that
   * entry's amount; the latest entry on or before an occurrence wins (SM-41).
   */
  amountChanges?: AmountChange[]
  notes?: string
}

/** What the lender does after an overpayment: keep the payment and end sooner, or keep the term and pay less. */
export type RepaymentEffect = 'shorten' | 'lower'

export interface CapitalRepayment {
  id: string
  date: string // ISO yyyy-mm-dd
  amount: number // always positive
  effect: RepaymentEffect
  /** Set when the repayment came from a statement import. */
  importId?: string
}

export interface AmountChange {
  from: string // ISO yyyy-mm-dd, inclusive
  amount: number // always positive
}

// ---- Variable budgeted spend (groceries, fuel, etc.) ----
export interface BudgetItem {
  id: string
  kind: 'budget'
  name: string
  amount: number // forecast amount per occurrence
  frequency: 'weekly' | 'monthly'
  dateRule?: DateRule // monthly
  dayOfWeek?: number // weekly
  category?: string
  active: boolean
  notes?: string
  /**
   * Merchants that statement imports match to this budget, as `merchantKey`
   * gives them ("TESCO STORES"). Learnt from what the person matches, so the
   * next import suggests it. On the item rather than the plan so that a tab
   * on an older build, which passes items through untouched, can't drop it.
   */
  matchPatterns?: string[]
}

// ---- One-off transactions ----
export interface OneOffTransaction {
  id: string
  kind: 'oneoff'
  name: string
  amount: number // positive = money in, negative = money out
  date: string // ISO yyyy-mm-dd
  category?: string
  notes?: string
  /** Set when it came from a bank statement, so importing the same rows again skips them. */
  importId?: string
  /** The loan (an ExpenseRule with isDebt) this payment went against, e.g. settling it early (SM-40). */
  loanId?: string
}

// ---- Balance adjustments (reconciliations) ----
// A correction against the plan as of a date — e.g. reconciling against your
// actual bank balance after overspending or underspending. Mechanically it
// behaves like a one-off transaction (it shifts every day's balance from
// its date forward), but it's tracked separately so it can carry a reason,
// be toggled on/off, and be excluded entirely when viewing the "strict"
// (as-budgeted) plan.
export interface BalanceAdjustment {
  id: string
  kind: 'adjustment'
  date: string // ISO yyyy-mm-dd — applies from this date forward
  delta: number // signed: positive = ahead of plan, negative = behind (overspent)
  note?: string // why the deviation happened — context, not an excuse
  active: boolean
}

// ---- Recorded actuals ----
// What a single occurrence of a recurring item really cost, as opposed to
// what was forecast. Keyed to the occurrence's own date, so a weekly
// grocery budget can be £100 forecast but £85 / £97 / £105 in practice —
// giving a running variance rather than a point-in-time "we went over".
export interface ActualEntry {
  id: string
  sourceId: string // the budget, fixed outgoing or income this belongs to
  date: string // ISO yyyy-mm-dd — the occurrence date it replaces
  amount: number // what was actually spent (or, for income, received), always positive
  note?: string
  /** The statement rows summed into `amount`, so importing them again doesn't count them twice. */
  importIds?: string[]
}

// ---- Savings targets ----
// The RAG thresholds mark danger. A target marks the opposite: a level worth
// reaching and holding. One shape covers all three cases —
//   • "show me when I hit £x"            → consecutiveDays: 1
//   • "when I've held £x for y days"     → consecutiveDays: y
//   • "…and then raise the bar by £z"    → raiseBy: z
export interface BalanceTarget {
  id: string
  name: string
  amount: number // the level to reach
  consecutiveDays: number // how long it must be held before it counts (1 = on the day)
  /** When set, completing a run raises the level by this much and the run restarts. */
  raiseBy?: number
  /** How qualifying days are marked on the calendar. */
  style: 'gold' | 'pulse'
  active: boolean
}

// ---- Transfers between accounts ----
// Money moving from one of the plan's accounts to another — a standing order
// into savings, topping up a joint account. One entry rather than an outgoing
// on one side and an income on the other, so the two halves can't drift apart.
// Held on the plan, not an account, because it belongs to both.
export interface Transfer {
  id: string
  kind: 'transfer'
  name: string
  amount: number // always positive; direction comes from the two account ids
  fromAccountId: string
  toAccountId: string
  /** 'once' moves money on `date`; anything else repeats like an income rule. */
  frequency: Frequency | 'once'
  date?: string // ISO yyyy-mm-dd, used when frequency === 'once'
  dateRule?: DateRule // monthly/yearly
  monthOfYear?: number // yearly
  dayOfWeek?: number // weekly/four_weekly
  startDate?: string
  endDate?: string
  active: boolean
  notes?: string
}

/**
 * One account's side of a transfer: money out of the account it's from, money
 * in to the account it's to. What the engine and the sidebar read, so neither
 * has to know about the other account.
 */
export interface AccountTransfer extends Transfer {
  direction: 'in' | 'out'
  /** The account on the other side, by name, for labels. */
  counterpartyName: string
}

export type AnyItem = IncomeRule | ExpenseRule | BudgetItem | OneOffTransaction | BalanceAdjustment | Transfer

// ---- RAG thresholds ----
export interface RagThresholds {
  /** Balances at or below this are "bad" (red) */
  bad: number
  /** Balances at or below this (and above bad) are "medium" (amber). Above = "good". */
  medium: number
}

export type RagLevel = 'good' | 'medium' | 'bad'

/** Which list the sidebar is currently showing. */
export type SidebarTab = 'income' | 'expenses' | 'budgets' | 'oneoffs' | 'transfers' | 'adjustments' | 'targets'

// ---- Spending per shop ----
// What imported statements showed was spent at one shop in one calendar
// month (SM-19). Actuals sum a budget's payments together and lose which shop
// each came from; this keeps it, so a person's spending at Tesco can be set
// against the anonymous average. Their own data, kept in their own plan.
export interface ShopMonth {
  /** The statement's matching key, as `merchantKey` makes it. */
  merchant: string
  /** YYYY-MM */
  month: string
  visits: number
  /** Positive, in the plan's currency. */
  amount: number
}

/** Everything that feeds one account's forecast. */
export interface AccountItems {
  incomes: IncomeRule[]
  expenses: ExpenseRule[]
  budgets: BudgetItem[]
  oneOffs: OneOffTransaction[]
  adjustments: BalanceAdjustment[]
  actuals: ActualEntry[]
  targets: BalanceTarget[]
  /** Absent until a statement import records some. Not part of the forecast. */
  shopSpending?: ShopMonth[]
}

/** Settings that belong to one account rather than the whole plan. */
export interface AccountSettings {
  startingBalance: number
  startingBalanceDate: string // ISO yyyy-mm-dd — the balance is as-of this date
  /** A savings account and a current account are tight at very different levels. */
  ragThresholds: RagThresholds
}

// ---- Interest ----
/**
 * One band of a tiered rate. Banded, not whole-balance: with bands of "up to
 * £500 at 4.5%" and "the rest at 1.5%", a £2,000 balance earns 4.5% on its
 * first £500 and 1.5% on the other £1,500.
 */
export interface InterestBand {
  /** The top of this band. Absent on the last band, which covers the rest. */
  upTo?: number
  /** Gross annual rate, as a percentage — 4.5 means 4.5% a year. */
  rate: number
}

/**
 * The bands in force from a date until the next rate takes over. A rate change
 * is a new entry rather than an edit, so interest earned before it stays as it was.
 */
export interface InterestRate {
  id: string
  /**
   * ISO yyyy-mm-dd. Absent on the rate the account started with, which
   * applies from the beginning; every later change has a date.
   */
  from?: string
  /** Ordered by `upTo`; the last has none. */
  bands: InterestBand[]
}

export interface InterestSettings {
  enabled: boolean
  /** Day of the month the accrued interest is paid in. Clamped to short months. */
  paymentDay: number | 'last'
  rates: InterestRate[]
}

/**
 * One real-world account — a current account, a joint account, savings. Named
 * BankAccount in code because "account" already means the sign-in account.
 */
export interface BankAccount extends AccountItems {
  id: string
  name: string
  /**
   * Whether this account counts towards the combined balance. Off for things
   * that aren't really spendable — a pension, say, or money held for someone else.
   */
  includeInTotal: boolean
  settings: AccountSettings
  /** Absent on an account that has never had interest set up. */
  interest?: InterestSettings
  /**
   * An irreversible fingerprint of the sort code and account number its
   * statements print (SM-25): an HMAC under a secret only the server has.
   * Set by the first import that has them, to match later statements to it.
   */
  statementFingerprint?: string
}

/** The plan-wide low-balance warning. */
export interface LowBalanceAlert {
  enabled: boolean
  /** How many days ahead to look, today included. */
  days: number
  /** Warn below this. Absent means the combined balance's "tight" level. */
  threshold?: number
}

/** On for a plan that has never changed it: a month ahead, at the combined balance's "tight" level. */
export const DEFAULT_LOW_BALANCE_ALERT: LowBalanceAlert = { enabled: true, days: 30 }

/** How far ahead the warning can look: from tomorrow to the year the overview charts. */
export const LOW_BALANCE_DAYS = { min: 1, max: 365 }

/** Settings shared by every account in the plan. */
export interface PlanSettings {
  currencySymbol: string
  /**
   * If true, project balances backward for dates before startingBalanceDate
   * too (useful for glancing at "what would the trend have looked like").
   * These are necessarily speculative — real transactions before the anchor
   * date aren't recorded — so this defaults to false and those days are
   * shown as disabled/greyed out instead.
   */
  projectHistory: boolean
  /** RAG thresholds for the combined balance across every included account. */
  combinedRagThresholds: RagThresholds
  /**
   * Absent on a plan that has never changed it, which reads as the default
   * (see lib/lowBalance). Kept inside `settings`, which the server stores
   * without checking its keys, so it needed no schema change.
   */
  lowBalanceAlert?: LowBalanceAlert
}

export const PLAN_SCHEMA_VERSION = 3

/**
 * The saved document: what the server stores per slot, and what Export writes.
 *
 * Version 1 was a single account's `AppState` at the top level; version 2
 * wrapped it in a list of accounts; version 3 added transfers between them.
 * The server migrates stored documents forward (see the backend's
 * Version20260927120000 and Version20261001120000); `normalizePlan` in
 * lib/storage does the same for anything the server never held — an old
 * export file, a cache written before the upgrade.
 */
export interface Plan {
  schemaVersion: typeof PLAN_SCHEMA_VERSION
  settings: PlanSettings
  /** Never empty. The first one is where the app opens for a new plan. */
  accounts: BankAccount[]
  /** Every one names two different accounts that are both in `accounts`. */
  transfers: Transfer[]
}

/** One account's settings with the plan-wide ones folded in — what the engine reads. */
export interface AppState extends AccountItems {
  settings: AccountSettings & Pick<PlanSettings, 'currencySymbol' | 'projectHistory'>
  interest?: InterestSettings
  /** This account's side of each transfer that touches it. Absent reads as none. */
  transfers?: AccountTransfer[]
}

export type AppSettings = AppState['settings']

// ---- Computed cashflow output ----
export interface TransactionOccurrence {
  sourceId: string
  /** 'interest' is generated by the engine rather than entered — there's no item behind it. */
  sourceKind: AnyItem['kind'] | 'interest'
  name: string
  amount: number // signed: +in, -out
  category?: string
  notes?: string
  /** True when `amount` came from a recorded actual rather than the forecast. */
  isActual?: boolean
  /** The forecast amount this occurrence would have used (always positive). */
  budgeted?: number
  /** Set on the combined view, where one day holds transactions from several accounts. */
  accountName?: string
}

/** A target level reached (and held long enough) on a given day. */
export interface TargetHit {
  targetId: string
  name: string
  /** The level actually reached — higher than the target's base amount once it has ratcheted up. */
  level: number
  style: BalanceTarget['style']
}

export interface DayResult {
  date: string // ISO yyyy-mm-dd
  transactions: TransactionOccurrence[]
  netChange: number
  balance: number // closing balance at end of this day
  rag: RagLevel
  targetHits: TargetHit[]
}
